/**
 * IndustryFreshnessBadge — small status pill showing how stale the linked
 * Industry Research is.
 *
 * Thresholds:
 *   < 30 days  → green   "Fresh"
 *   30–90 days → amber   "Recent"
 *   > 90 days  → red     "Stale"
 *
 * Also displays the semantic version string when available.
 */
import React from 'react';
import { Clock } from 'lucide-react';

interface IndustryFreshnessBadgeProps {
  ageInDays?: number | null;
  versionString?: string | null;
}

const IndustryFreshnessBadge: React.FC<IndustryFreshnessBadgeProps> = ({
  ageInDays,
  versionString,
}) => {
  if (ageInDays == null && !versionString) return null;

  let label = 'Unknown age';
  let tone = 'bg-neutral-100 text-neutral-700 border-neutral-200';

  if (ageInDays != null) {
    if (ageInDays < 30) {
      label = `Fresh — ${ageInDays}d`;
      tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    } else if (ageInDays < 90) {
      label = `Recent — ${ageInDays}d`;
      tone = 'bg-amber-50 text-amber-700 border-amber-200';
    } else {
      label = `Stale — ${ageInDays}d`;
      tone = 'bg-red-50 text-red-700 border-red-200';
    }
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${tone}`}
      >
        <Clock className="w-3 h-3" />
        {label}
      </span>
      {versionString && (
        <span className="text-xs text-neutral-500">v{versionString.replace(/^v/, '')}</span>
      )}
    </div>
  );
};

export default IndustryFreshnessBadge;
